import { useEffect, useMemo, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Copy, MapPin, Warning, GitMerge, ArrowRight, X, Hash,
  ArrowsClockwise, Broom, Archive, CheckCircle,
} from "@phosphor-icons/react";

/**
 * Duplicates Queue — two axes:
 *   1. by_wo:      cross-importer WO# doubles (legacy vs new CSV import)
 *                  → offers a big "Auto-merge all" button
 *   2. by_address: original heuristic — 2+ open jobs at same (address+unit)
 * All merges use the same /jobs/merge pipeline — photos, notes, checklists
 * from every child are preserved on the master.
 */
export default function AdminDuplicates() {
  const [scan, setScan] = useState({ groups: [], by_wo: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [autoBusy, setAutoBusy] = useState(false);
  const [mergeGroup, setMergeGroup] = useState(null);       // address-based
  const [mergeWoGroup, setMergeWoGroup] = useState(null);   // WO#-based

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/duplicates-queue");
      // Back-compat: server may return array (old) or object (new)
      const data = Array.isArray(r.data)
        ? { groups: r.data, by_wo: [], counts: { address_groups: r.data.length, wo_groups: 0, wo_extra_jobs: 0 } }
        : r.data;
      setScan(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load duplicates");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runAutoMerge = async (dryRun) => {
    if (!scan.by_wo.length) return;
    if (!dryRun && !window.confirm(
      `Merge every WO# double? ${scan.counts.wo_groups} groups · ${scan.counts.wo_extra_jobs} extra jobs will be folded into their master. Idempotent — safe to re-run.`,
    )) return;
    setAutoBusy(true);
    const t = toast.loading(dryRun ? "Simulating…" : "Merging every WO# double…");
    try {
      const r = await api.post("/duplicates-queue/auto-merge-by-wo", { wo_numbers: [], dry_run: !!dryRun });
      if (dryRun) {
        toast.info(`Would merge ${r.data.would_merge_groups} groups · ${r.data.would_merge_children} children`, { id: t });
      } else {
        toast.success(
          `Merged ${r.data.merged_groups} groups · ${r.data.merged_children} children folded${r.data.skipped ? ` · ${r.data.skipped} skipped` : ""}`,
          { id: t },
        );
        await load();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Auto-merge failed", { id: t });
    } finally { setAutoBusy(false); }
  };

  return (
    <AdminShell>
      <div className="p-6 md:p-8">
        <div className="flex items-center gap-3 flex-wrap">
          <Copy weight="fill" size={28} className="text-fuchsia-600"/>
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Duplicate Work Orders</div>
            <h1 className="font-display font-black text-3xl">Duplicates Queue</h1>
          </div>
          <Link to="/admin/jobs" className="ml-auto text-[#00C4CC] font-black uppercase text-xs tracking-wider">← Back to Jobs</Link>
        </div>
        <p className="mt-2 text-slate-500 text-sm max-w-2xl">
          Two kinds of doubles get caught here. WO# doubles (from re-imports across schemas) can be
          nuked in one click. Address doubles (same tenant/unit) are reviewed per row.
        </p>

        {loading && <div className="mt-8 font-bold text-slate-500">Loading…</div>}

        {!loading && (scan.by_wo?.length || 0) === 0 && (scan.groups?.length || 0) === 0 && (
          <div className="mt-8 rugged-card p-8 text-center" data-testid="dupes-empty">
            <CheckCircle weight="fill" size={40} className="mx-auto text-emerald-500"/>
            <div className="mt-3 font-display font-black text-2xl">Clean 🎉</div>
            <p className="mt-2 text-slate-500 text-sm">No duplicates detected across either axis.</p>
          </div>
        )}

        {/* ═════════ WO # doubles (auto-merge one-shot) ═════════ */}
        {!loading && (scan.by_wo?.length || 0) > 0 && (
          <section className="mt-6" data-testid="wo-doubles-section">
            <div className="rugged-card p-5 bg-gradient-to-br from-amber-50 to-white border-amber-500">
              <div className="flex items-start gap-3 flex-wrap">
                <Hash weight="fill" size={26} className="text-amber-700 shrink-0 mt-1"/>
                <div className="flex-1 min-w-[220px]">
                  <div className="font-display font-black text-xl uppercase text-amber-900">
                    Cross-Importer WO# Doubles
                  </div>
                  <p className="mt-1 text-sm text-slate-700">
                    Jobs sharing the same Work Order # across the old legacy importer and the new AppFolio
                    CSV importer. This is the classic re-import bug. Auto-merge picks the newest non-archived
                    row as the master and folds every twin in — <b>photos, notes and checklists are preserved</b>.
                  </p>
                  <div className="mt-2 text-[11px] text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
                    <span><b className="text-amber-800">{scan.counts?.wo_groups || 0}</b> groups</span>
                    <span><b className="text-fuchsia-700">{scan.counts?.wo_extra_jobs || 0}</b> extra jobs will be folded</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => runAutoMerge(true)} disabled={autoBusy}
                    data-testid="wo-dryrun-btn"
                    className="h-11 px-3 bg-white border-2 border-amber-500 text-amber-800 font-black uppercase tracking-widest text-xs disabled:opacity-50 inline-flex items-center gap-1">
                    Dry-run
                  </button>
                  <button onClick={() => runAutoMerge(false)} disabled={autoBusy}
                    data-testid="wo-automerge-btn"
                    className="h-11 px-4 bg-amber-600 hover:bg-amber-700 text-white font-black uppercase tracking-widest text-xs disabled:opacity-50 inline-flex items-center gap-1 shadow-[4px_4px_0px_#0f172a]">
                    {autoBusy ? <ArrowsClockwise className="animate-spin" size={14}/> : <Broom weight="fill" size={14}/>}
                    Merge all
                  </button>
                </div>
              </div>

              {/* Detail list */}
              <details className="mt-3">
                <summary className="cursor-pointer text-[10px] font-black uppercase tracking-widest text-amber-800">
                  Show {scan.by_wo.length} WO group{scan.by_wo.length === 1 ? "" : "s"}
                </summary>
                <div className="mt-2 space-y-2 max-h-[52vh] overflow-y-auto">
                  {scan.by_wo.map((g, idx) => (
                    <div key={g.wo} className="p-3 bg-white border-2 border-slate-200"
                         data-testid={`wo-group-${idx}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center h-6 px-2 bg-[#0f172a] text-white text-[10px] font-black uppercase tracking-widest font-mono">
                          WO # {g.wo}
                        </span>
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                          {g.jobs.length} copies
                        </span>
                        <button onClick={() => setMergeWoGroup(g)}
                          data-testid={`wo-group-merge-${idx}`}
                          className="ml-auto h-8 px-3 bg-[#0f172a] hover:bg-fuchsia-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
                          <GitMerge weight="bold" size={11}/> Review
                        </button>
                      </div>
                      <div className="mt-2 space-y-1">
                        {g.jobs.map((j, ji) => (
                          <div key={j.id} className="flex items-center gap-2 text-xs">
                            <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${
                              ji === 0 ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"
                            }`}>
                              {ji === 0 ? "master" : "child"}
                            </span>
                            {j.archived && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-slate-800 text-white">
                                <Archive size={9}/> archived
                              </span>
                            )}
                            {j.legacy_import && (
                              <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-cyan-100 text-cyan-800">legacy</span>
                            )}
                            {j.source === "appfolio" && (
                              <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-fuchsia-100 text-fuchsia-800">appfolio</span>
                            )}
                            <Link to={`/admin/jobs/${j.id}`} className="font-black text-slate-800 hover:text-fuchsia-700 truncate flex-1"
                              data-testid={`wo-group-${idx}-job-${ji}`}>
                              {j.title || "(no title)"}
                            </Link>
                            <span className="text-slate-400 text-[10px] whitespace-nowrap">
                              {(j.created_at || "").slice(0,10)} · {j.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            </div>
          </section>
        )}

        {/* ═════════ Address doubles (existing) ═════════ */}
        {!loading && (scan.groups?.length || 0) > 0 && (
          <section className="mt-6" data-testid="address-doubles-section">
            <div className="mb-3 flex items-center gap-2 flex-wrap">
              <MapPin weight="fill" size={20} className="text-slate-500"/>
              <div className="font-display font-black text-xl uppercase">Same-Address Doubles</div>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                {scan.groups.length} group{scan.groups.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="space-y-4">
              {scan.groups.map((g, idx) => (
                <div key={`${g.address}-${g.unit || ""}-${idx}`} className="rugged-card p-5" data-testid={`dupes-group-${idx}`}>
                  <div className="flex items-start gap-3">
                    <MapPin weight="fill" size={22} className="text-slate-500 mt-1"/>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-black text-xl">
                        {g.address}{g.unit && <span className="ml-2 px-2 py-0.5 bg-fuchsia-600 text-white font-black uppercase text-[10px] tracking-widest rounded-sm">#{g.unit}</span>}
                      </div>
                      <div className="text-xs text-slate-500 font-black uppercase tracking-widest">{g.jobs.length} open work orders</div>
                    </div>
                    <button onClick={() => setMergeGroup(g)} data-testid={`dupes-merge-open-${idx}`}
                      className="tactile-btn h-10 px-4 bg-[#0f172a] text-white font-black uppercase text-xs tracking-widest inline-flex items-center gap-1.5 hover:bg-fuchsia-700">
                      <GitMerge weight="bold" size={14}/> Merge
                    </button>
                  </div>
                  <div className="mt-3 space-y-1.5">
                    {g.jobs.map((j, ji) => (
                      <Link key={j.id} to={`/admin/jobs/${j.id}`}
                        data-testid={`dupes-job-${idx}-${ji}`}
                        className="flex items-center gap-2 p-2 border border-slate-200 hover:bg-slate-50 group">
                        <span className="inline-flex items-center h-5 px-1.5 bg-[#0f172a] text-white text-[10px] font-black uppercase tracking-widest font-mono">
                          WO# {j.wo || j.id.slice(-6)}
                        </span>
                        {j.life_safety && (
                          <span className="inline-flex items-center gap-1 h-5 px-1.5 bg-red-600 text-white text-[10px] font-black uppercase tracking-widest">
                            <Warning weight="fill" size={10}/> Safety
                          </span>
                        )}
                        <span className="font-black truncate flex-1">{j.title}</span>
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 whitespace-nowrap">
                          {(j.created_at || "").slice(0, 10)} · {j.status}{j.assigned_to_name ? ` · ${j.assigned_to_name}` : ""}
                        </span>
                        <ArrowRight weight="bold" size={14} className="text-slate-300 group-hover:text-[#0f172a]"/>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {mergeGroup   && <MergeModal group={mergeGroup}   onClose={() => setMergeGroup(null)}   onDone={load}/>}
      {mergeWoGroup && <MergeModal group={mergeWoGroup} onClose={() => setMergeWoGroup(null)} onDone={load} isWoGroup/>}
    </AdminShell>
  );
}

function MergeModal({ group, onClose, onDone, isWoGroup }) {
  // Sort: prefer non-archived, then oldest first, so master pick is natural
  const sorted = useMemo(() => {
    return [...group.jobs].sort((a, b) =>
      (a.archived === b.archived ? 0 : a.archived ? 1 : -1)
      || (a.created_at || "").localeCompare(b.created_at || ""),
    );
  }, [group]);
  const [masterId, setMasterId] = useState(sorted[0]?.id || "");
  const [childIds, setChildIds] = useState(new Set(sorted.slice(1).map(j => j.id)));
  const [busy, setBusy] = useState(false);

  const toggleChild = (id) => {
    setChildIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const changeMaster = (newId) => {
    setMasterId(newId);
    setChildIds(prev => {
      const next = new Set(prev);
      next.delete(newId);
      return next;
    });
  };

  const submit = async () => {
    if (!masterId || childIds.size === 0) {
      toast.error("Pick a master and at least one child to merge");
      return;
    }
    setBusy(true);
    try {
      await api.post("/jobs/merge", { master_id: masterId, child_ids: Array.from(childIds) });
      toast.success(`Merged ${childIds.size} work order${childIds.size === 1 ? "" : "s"} into master`);
      onClose();
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not merge");
    } finally { setBusy(false); }
  };

  const header = isWoGroup
    ? `WO # ${group.wo}`
    : `${group.address}${group.unit ? ` · #${group.unit}` : ""}`;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-lg border-4 border-[#0f172a] shadow-[6px_6px_0px_#00C4CC] max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()} data-testid="merge-modal">
        <div className="p-4 border-b-2 border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Merge Duplicates</div>
            <div className="font-display font-black text-xl truncate">{header}</div>
          </div>
          <button onClick={onClose} className="p-1"><X weight="bold" size={22}/></button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-xs text-slate-600">
            Pick the <b>master</b> (the WO everything else folds into). Photos, checklists, notes and descriptions from every child are appended — nothing gets lost. Children stay viewable but marked <span className="font-mono bg-slate-100 px-1">merged</span>.
          </p>
          <div className="space-y-2">
            {sorted.map((j) => {
              const isMaster = j.id === masterId;
              const isChild = childIds.has(j.id);
              return (
                <div key={j.id} className={`p-2 border-2 ${isMaster ? "border-[#00C4CC] bg-cyan-50" : isChild ? "border-fuchsia-400 bg-fuchsia-50" : "border-slate-200"}`}
                  data-testid={`merge-row-${j.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <label className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest cursor-pointer">
                      <input type="radio" name="master" checked={isMaster} onChange={() => changeMaster(j.id)}
                        data-testid={`merge-master-${j.id}`}/>
                      Master
                    </label>
                    <label className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest cursor-pointer">
                      <input type="checkbox" checked={isChild} disabled={isMaster}
                        onChange={() => toggleChild(j.id)}
                        data-testid={`merge-child-${j.id}`}/>
                      Fold in
                    </label>
                    {j.archived && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-slate-800 text-white">
                        <Archive size={9}/> archived
                      </span>
                    )}
                    <span className="ml-auto inline-flex items-center h-5 px-1.5 bg-[#0f172a] text-white text-[10px] font-black uppercase tracking-widest font-mono">
                      WO# {j.wo || j.id.slice(-6)}
                    </span>
                  </div>
                  <div className="mt-1 font-black text-sm truncate">{j.title || "(no title)"}</div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                    {j.address && <span>{j.address}{j.unit ? ` · #${j.unit}` : ""} · </span>}
                    {(j.created_at || "").slice(0, 10)} · {j.status}{j.assigned_to_name ? ` · ${j.assigned_to_name}` : ""}
                  </div>
                </div>
              );
            })}
          </div>
          <button onClick={submit} disabled={busy || !masterId || childIds.size === 0}
            data-testid="merge-submit"
            className="w-full h-12 bg-gradient-to-r from-[#0f172a] to-fuchsia-600 text-white font-black uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
            <GitMerge weight="fill" size={16}/> {busy ? "Merging…" : `Merge ${childIds.size} into master`}
          </button>
        </div>
      </div>
    </div>
  );
}
